/**
 * Auth page — Sign in and Create account in one place.
 *
 * /login and /signup both render this same component (Login.js and Signup.js re-export
 * it), so switching tabs only swaps the URL: the background scene keeps running and the
 * glass card slides between the two forms instead of the whole page remounting.
 *
 * Background: a procedural floating-island scene (pages/auth/authScene.js).
 * Behaviour (login, 403 → resend verification, signup validation, verify-email state,
 * Google OAuth, session-expired notice) matches the previous Login/Signup pages.
 */
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../components/AuthProvider';
import { login as loginService, signup as signupService, resendVerification } from '../services/authService';
import { getErrorMessage } from '../lib/apiError';
import { createAuthScene } from './auth/authScene';
import './auth/AuthPage.css';

const LOGO = '/assets/Arantree_logo_upscaled.png';

const FEATURES = [
  {
    title: 'Batch Emission Processing',
    description: 'Upload your shipment data in Excel or CSV — our engine auto-maps columns, resolves vehicle types, and calculates emissions for all rows at once',
    path: 'M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12',
  },
  {
    title: 'Multi-Modal Support',
    description: 'Road, rail, air, and sea — with detailed vehicle types, fuel options, laden status, and radiative forcing for accurate Scope 3 reporting',
    path: 'M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7',
  },
  {
    title: 'Dashboard & Reports',
    description: 'Track emission trends by transport mode, filter by GHG category, and export detailed CSV reports for sustainability compliance',
    path: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
  },
];

const PASSWORD_RULES = [
  { key: 'len', label: 'Min 8 characters', test: (v) => v.length >= 8 },
  { key: 'upper', label: '1 uppercase', test: (v) => /[A-Z]/.test(v) },
  { key: 'num', label: '1 number', test: (v) => /[0-9]/.test(v) },
];

const goGoogle = () => {
  window.location.href = (process.env.REACT_APP_BACKEND_URL || 'http://localhost:5001') + '/api/auth/google';
};

/* ---------- small presentational pieces ---------- */
const Check = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 13l4 4L19 7" /></svg>
);
const ErrorIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
);
const WarnIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
);
const Spinner = () => <span className="cra-spinner" aria-hidden="true" />;

const GoogleButton = () => (
  <button type="button" className="cra-btn-google" onClick={goGoogle}>
    <svg viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z" />
      <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z" />
      <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z" />
      <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z" />
    </svg>
    Continue with Google
  </button>
);

const PasswordInput = ({ id, name, value, onChange, placeholder, autoComplete, describedBy }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="cra-input-wrap">
      <input
        id={id} name={name} type={show ? 'text' : 'password'} required value={value} onChange={onChange}
        placeholder={placeholder} autoComplete={autoComplete} aria-describedby={describedBy}
      />
      <button type="button" className="cra-eye" aria-label={show ? 'Hide password' : 'Show password'} onClick={() => setShow((s) => !s)}>
        {show ? (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 19c-6.4 0-10-7-10-7a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c6.4 0 10 7 10 7a18.5 18.5 0 0 1-2.16 3.19M1 1l22 22" /><path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" /></svg>
        ) : (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z" /><circle cx="12" cy="12" r="3" /></svg>
        )}
      </button>
    </div>
  );
};

/* Headline split into characters for a staggered blur-in; the accent phrase gets a
   teal → green ramp painted per character so the animation and the colour coexist. */
const Headline = () => {
  let i = 0;
  const words = (text, accent) => {
    const chars = [...text.replace(/\s+/g, '')].length;
    let k = 0;
    return text.split(' ').map((word, wi) => (
      <React.Fragment key={wi}>
        {wi > 0 && ' '}
        <span className="cra-w">
          {[...word].map((ch, ci) => {
            const style = { '--i': i++ };
            if (accent) {
              const f = k++ / Math.max(1, chars - 1);
              const mix = (a, b) => Math.round(a + (b - a) * f);
              style.color = `rgb(${mix(14, 47)},${mix(138, 143)},${mix(125, 87)})`;
            }
            return <span key={ci} className="cra-c" style={style}>{ch}</span>;
          })}
        </span>
      </React.Fragment>
    ));
  };
  return (
    <h1 className="cra-headline" aria-label="Automate Your Emission Reporting">
      <span aria-hidden="true">{words('Automate Your', false)}<br />{words('Emission Reporting', true)}</span>
    </h1>
  );
};

/* Critically damped spring (Apple's default, damping 1.0) expressed as CSS linear(). */
function springEasing() {
  if (typeof CSS === 'undefined' || !CSS.supports('transition-timing-function', 'linear(0, 1)')) return null;
  const pts = [];
  for (let i = 0; i <= 40; i++) { const x = i / 40; pts.push(+(1 - (1 + 9 * x) * Math.exp(-9 * x)).toFixed(4)); }
  pts[pts.length - 1] = 1;
  return `linear(${pts.join(',')})`;
}

const Auth = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const mode = location.pathname.startsWith('/signup') ? 'signup' : 'signin';
  const isSignup = mode === 'signup';
  const expired = new URLSearchParams(location.search).get('expired');

  /* ----- sign in state (unchanged behaviour) ----- */
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [unverified, setUnverified] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  /* ----- sign up state (unchanged behaviour) ----- */
  const [signupData, setSignupData] = useState({ email: '', password: '', confirmPassword: '', fullName: '', companyName: '' });
  const [signupError, setSignupError] = useState('');
  const [signupLoading, setSignupLoading] = useState(false);
  const [signupSuccess, setSignupSuccess] = useState(false);

  const rootRef = useRef(null);
  const canvasRef = useRef(null);
  const heroRef = useRef(null);
  const heroInnerRef = useRef(null);
  const navRef = useRef(null);
  const cardRef = useRef(null);
  const panesRef = useRef(null);
  const paneInRef = useRef(null);
  const paneUpRef = useRef(null);
  const [scrolled, setScrolled] = useState(false);

  /* ----- background scene ----- */
  useEffect(() => {
    const scene = createAuthScene(canvasRef.current, { getAnchor: () => heroRef.current?.getBoundingClientRect() });
    return () => scene.destroy();
  }, []);

  /* ----- Inter for non-Apple platforms (Apple devices use SF via -apple-system) ----- */
  useEffect(() => {
    if (document.getElementById('cra-inter-font')) return;
    const link = document.createElement('link');
    link.id = 'cra-inter-font'; link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Inter:opsz,wght@14..32,400..700&display=swap';
    document.head.appendChild(link);
  }, []);

  /* ----- spring easing + scroll-linked hero + reveal-on-enter ----- */
  useEffect(() => {
    const ease = springEasing();
    if (ease && rootRef.current) rootRef.current.style.setProperty('--ease-spring', ease);

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let queued = false;
    const onScroll = () => {
      if (queued) return; queued = true;
      requestAnimationFrame(() => {
        queued = false;
        const y = window.scrollY;
        setScrolled(y > 24);
        const el = heroInnerRef.current;
        if (!el) return;
        if (!reduce && window.innerWidth >= 1024) {
          const p = Math.min(1, y / (window.innerHeight * 0.65));
          el.style.opacity = String(1 - p);
          el.style.transform = `translateY(${-p * 70}px) scale(${1 - p * 0.04})`;
          el.style.filter = p > 0.01 ? `blur(${p * 6}px)` : '';
        } else {
          el.style.opacity = ''; el.style.transform = ''; el.style.filter = '';
        }
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); } });
    }, { threshold: 0.18, rootMargin: '0px 0px -6% 0px' });
    rootRef.current.querySelectorAll('.cra-reveal').forEach((el) => io.observe(el));

    return () => { window.removeEventListener('scroll', onScroll); io.disconnect(); };
  }, []);

  /* ----- card height follows the active pane (spring transition in CSS) ----- */
  useLayoutEffect(() => {
    const sync = () => {
      const active = isSignup ? paneUpRef.current : paneInRef.current;
      if (active && panesRef.current) panesRef.current.style.height = active.offsetHeight + 'px';
    };
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(paneInRef.current); ro.observe(paneUpRef.current);
    return () => ro.disconnect();
  }, [isSignup]);

  const setMode = (next, { focus = false } = {}) => {
    if (next !== mode) {
      navigate({ pathname: next === 'signup' ? '/signup' : '/login', search: location.search }, { replace: true, state: location.state });
    }
    if (focus) {
      if (window.innerWidth < 1024) cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setTimeout(() => {
        const pane = next === 'signup' ? paneUpRef.current : paneInRef.current;
        pane?.querySelector('input')?.focus({ preventScroll: true });
      }, 320);
    }
  };

  const onSegKey = (e) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const next = e.key === 'ArrowRight' ? 'signup' : 'signin';
    setMode(next);
    document.getElementById(next === 'signup' ? 'cra-tab-signup' : 'cra-tab-signin')?.focus();
  };

  const onCardPointer = (e) => {
    const el = cardRef.current; if (!el) return;
    const r = el.getBoundingClientRect();
    el.style.setProperty('--mx', (((e.clientX - r.left) / r.width) * 100).toFixed(1) + '%');
    el.style.setProperty('--my', (((e.clientY - r.top) / r.height) * 100).toFixed(1) + '%');
  };

  /* ----- sign in handlers ----- */
  const handleLoginChange = (e) => { setLoginData({ ...loginData, [e.target.name]: e.target.value }); setLoginError(''); };

  const handleResendVerification = async () => {
    setResending(true); setResendSuccess(false);
    try {
      await resendVerification(loginData.email);
      setResendSuccess(true);
    } catch (err) {
      setLoginError('Failed to resend verification email. Please try again.');
    } finally {
      setResending(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError(''); setLoginLoading(true);
    try {
      const response = await loginService(loginData.email, loginData.password);
      login(response.user, response.access_token);
      const from = location.state?.from?.pathname || '/';
      navigate(from, { replace: true });
    } catch (err) {
      console.error('Login error:', err);
      if (err.response?.status === 403) {
        setUnverified(true);
        setLoginError(getErrorMessage(err, 'Please verify your email to continue.'));
      } else {
        setUnverified(false);
        setLoginError(getErrorMessage(err, 'Login failed. Please check your credentials and try again.'));
      }
    } finally {
      setLoginLoading(false);
    }
  };

  /* ----- sign up handlers ----- */
  const handleSignupChange = (e) => { setSignupData({ ...signupData, [e.target.name]: e.target.value }); setSignupError(''); };

  const validatePassword = () => {
    if (signupData.password.length < 8) return 'Password must be at least 8 characters long';
    if (!/[A-Z]/.test(signupData.password)) return 'Password must contain at least one uppercase letter';
    if (!/[0-9]/.test(signupData.password)) return 'Password must contain at least one number';
    if (signupData.password !== signupData.confirmPassword) return 'Passwords do not match';
    return null;
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setSignupError(''); setSignupLoading(true);
    if (!signupData.email) { setSignupError('Please enter your email address'); setSignupLoading(false); return; }
    const passwordError = validatePassword();
    if (passwordError) { setSignupError(passwordError); setSignupLoading(false); return; }
    try {
      await signupService(signupData.email, signupData.password, signupData.fullName || null, signupData.companyName || null);
      setSignupSuccess(true); // no auto-redirect — the user must verify their email first
    } catch (err) {
      console.error('Signup error:', err);
      setSignupError(getErrorMessage(err, 'Failed to create account. Please try again.'));
    } finally {
      setSignupLoading(false);
    }
  };

  return (
    <div className="cr-auth" ref={rootRef}>
      <div className="cra-sky" aria-hidden="true" />
      <canvas className="cra-scene" ref={canvasRef} aria-hidden="true" />

      <header className={`cra-nav cra-glass${scrolled ? ' is-scrolled' : ''}`} ref={navRef}>
        <a className="cra-brand" href="#cra-hero" aria-label="CarbonRoute">
          <span className="cra-brand-name">CarbonRoute</span>
          <span className="cra-brand-sub">Supply Chain Emission Calculator</span>
        </a>
        <div className="cra-powered">
          <div className="cra-powered-text"><span>Powered by</span><strong>Arantree Consulting Services</strong></div>
          <img src={LOGO} alt="Arantree Consulting Services" />
        </div>
      </header>

      <div className="cra-layout">
        {/* ========== HERO ========== */}
        <section className="cra-hero" id="cra-hero" ref={heroRef} aria-label="CarbonRoute">
          <div className="cra-hero-inner" ref={heroInnerRef}>
            <div className="cra-chip cra-fade-up" style={{ '--d': '80ms' }}>
              <span className="cra-dot"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M1 3h15v13H1zM16 8h4l3 3v5h-7" /><circle cx="5.5" cy="18.5" r="2.5" /><circle cx="18.5" cy="18.5" r="2.5" /></svg></span>
              <span className="cra-arrow" aria-hidden="true">→</span>
              <span className="cra-dot"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10" /><path d="M2 12h20M12 2a15.3 15.3 0 0 1 0 20M12 2a15.3 15.3 0 0 0 0 20" /></svg></span>
              Road · Rail · Air · Sea
              <span className="cra-arrow" aria-hidden="true">→</span>
              <span className="cra-dot"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10Z" /><path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12" /></svg></span>
            </div>
            <Headline />
            <p className="cra-lede cra-fade-up" style={{ '--d': '900ms' }}>
              Upload your shipment data in Excel — AI maps your columns, calculates distances, and computes Scope 3 emissions across road, rail, air, and sea in minutes.
            </p>
            <div className="cra-cta-row cra-fade-up" style={{ '--d': '1100ms' }}>
              <a className="cra-btn cra-btn-dark" href="#cra-features">
                Explore features
                <span className="cra-ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 5v14M19 12l-7 7-7-7" /></svg></span>
              </a>
              <button className="cra-btn cra-btn-light" type="button" onClick={() => setMode('signup', { focus: true })}>
                Create an account
                <span className="cra-ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14M12 5l7 7-7 7" /></svg></span>
              </button>
            </div>
          </div>
        </section>

        {/* ========== AUTH CARD ========== */}
        <aside className="cra-auth-col" aria-label="Account">
          <div className="cra-card-auth cra-glass" ref={cardRef} data-mode={mode} onPointerMove={onCardPointer}>
            <div className="cra-seg" role="tablist" aria-label="Choose sign in or create account" onKeyDown={onSegKey}>
              <span className="cra-seg-pill" aria-hidden="true" />
              <button type="button" role="tab" id="cra-tab-signin" aria-controls="cra-pane-signin" aria-selected={!isSignup} tabIndex={isSignup ? -1 : 0} onClick={() => setMode('signin')}>Sign in</button>
              <button type="button" role="tab" id="cra-tab-signup" aria-controls="cra-pane-signup" aria-selected={isSignup} tabIndex={isSignup ? 0 : -1} onClick={() => setMode('signup')}>Create account</button>
            </div>

            <div className="cra-panes" ref={panesRef}>
              {/* ---------- Sign in ---------- */}
              <section className="cra-pane" id="cra-pane-signin" role="tabpanel" aria-labelledby="cra-tab-signin" data-pos={isSignup ? 'left' : 'center'} ref={paneInRef}>
                <div className="cra-auth-head">
                  <h2>Welcome back</h2>
                  <p>Sign in to continue to your dashboard</p>
                </div>

                {expired && (
                  <div className="cra-alert cra-alert-warn"><WarnIcon /><p>Your session has expired. Please sign in again.</p></div>
                )}

                {loginError && (
                  <div className={`cra-alert ${unverified ? 'cra-alert-warn' : 'cra-alert-error'}`} role="alert">
                    <ErrorIcon />
                    <div>
                      <p>{loginError}</p>
                      {unverified && (
                        <div className="cra-alert-sub">
                          {resendSuccess ? (
                            <p className="is-sent">Verification email sent! Check your inbox.</p>
                          ) : (
                            <button type="button" className="cra-linkish" onClick={handleResendVerification} disabled={resending}>
                              {resending ? 'Sending...' : 'Resend verification email'}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <form onSubmit={handleLogin}>
                  <div className="cra-field">
                    <label htmlFor="email">Email address</label>
                    <input id="email" name="email" type="email" autoComplete="email" required value={loginData.email} onChange={handleLoginChange} placeholder="your@email.com" />
                  </div>
                  <div className="cra-field">
                    <div className="cra-field-row">
                      <label htmlFor="password">Password</label>
                      <Link to="/forgot-password">Forgot password?</Link>
                    </div>
                    <PasswordInput id="password" name="password" value={loginData.password} onChange={handleLoginChange} placeholder="Enter your password" autoComplete="current-password" />
                  </div>
                  <button type="submit" className="cra-btn-primary" disabled={loginLoading}>
                    {loginLoading ? <><Spinner />Signing in...</> : 'Sign in'}
                  </button>
                </form>

                <div className="cra-divider">or</div>
                <GoogleButton />

                <p className="cra-switch-line">
                  New user? <button type="button" className="cra-linkish" onClick={() => setMode('signup', { focus: true })}>Create an account</button>
                </p>
                <p className="cra-legal">
                  By signing in, you agree to our <Link to="/terms">Terms of Service</Link> and <Link to="/privacy">Privacy Policy</Link>
                </p>
              </section>

              {/* ---------- Create account ---------- */}
              <section className="cra-pane" id="cra-pane-signup" role="tabpanel" aria-labelledby="cra-tab-signup" data-pos={isSignup ? 'center' : 'right'} ref={paneUpRef}>
                {signupSuccess ? (
                  <div className="cra-verify" aria-label="Verify email">
                    <div className="cra-verify-badge">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                    </div>
                    <div className="cra-auth-head" style={{ marginBottom: 0 }}>
                      <h2>Verify Your Email</h2>
                      <p>We've sent a verification link to</p>
                    </div>
                    <p className="cra-verify-email">{signupData.email}</p>
                    <div className="cra-steps">
                      <p>Next steps:</p>
                      <ol>
                        <li>Open the email from CarbonRoute</li>
                        <li>Click the verification link</li>
                        <li>Sign in to start calculating emissions</li>
                      </ol>
                      <small>The link expires in 24 hours. Check your spam folder if you don't see it.</small>
                    </div>
                    <button type="button" className="cra-btn-primary" onClick={() => { setSignupSuccess(false); setMode('signin', { focus: true }); }}>
                      Go to Sign In
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="cra-auth-head">
                      <h2>Create your account</h2>
                    </div>

                    {signupError && (
                      <div className="cra-alert cra-alert-error" role="alert"><ErrorIcon /><p>{signupError}</p></div>
                    )}

                    <form onSubmit={handleSignup}>
                      <div className="cra-field">
                        <label htmlFor="su-email">Email address <span className="cra-req">*</span></label>
                        <input id="su-email" name="email" type="email" autoComplete="email" required value={signupData.email} onChange={handleSignupChange} placeholder="your@email.com" />
                      </div>
                      <div className="cra-field">
                        <label htmlFor="su-fullName">Full Name <span className="cra-req">*</span></label>
                        <input id="su-fullName" name="fullName" type="text" autoComplete="name" required value={signupData.fullName} onChange={handleSignupChange} placeholder="Jane Doe" />
                      </div>
                      <div className="cra-field">
                        <label htmlFor="su-companyName">Company Name <span className="cra-req">*</span></label>
                        <input id="su-companyName" name="companyName" type="text" autoComplete="organization" required value={signupData.companyName} onChange={handleSignupChange} placeholder="Acme Logistics Inc." />
                      </div>
                      <div className="cra-field">
                        <label htmlFor="su-password">Password <span className="cra-req">*</span></label>
                        <PasswordInput id="su-password" name="password" value={signupData.password} onChange={handleSignupChange} placeholder="••••••••" autoComplete="new-password" describedBy="cra-pw-rules" />
                        <div className="cra-rules" id="cra-pw-rules" aria-label="Min 8 characters, 1 uppercase, 1 number">
                          {PASSWORD_RULES.map((r) => (
                            <span key={r.key} className={`cra-rule${r.test(signupData.password) ? ' is-ok' : ''}`}><i><Check /></i>{r.label}</span>
                          ))}
                        </div>
                      </div>
                      <div className="cra-field">
                        <label htmlFor="su-confirmPassword">Confirm Password <span className="cra-req">*</span></label>
                        <input id="su-confirmPassword" name="confirmPassword" type="password" autoComplete="new-password" required value={signupData.confirmPassword} onChange={handleSignupChange} placeholder="••••••••" />
                      </div>
                      <button type="submit" className="cra-btn-primary" disabled={signupLoading}>
                        {signupLoading ? <><Spinner />Creating account...</> : 'Create Account'}
                      </button>
                    </form>

                    <div className="cra-divider">or</div>
                    <GoogleButton />

                    <p className="cra-switch-line">
                      Already have an account? <button type="button" className="cra-linkish" onClick={() => setMode('signin', { focus: true })}>Sign in</button>
                    </p>
                    <p className="cra-legal">
                      By creating an account, you agree to our <Link to="/terms">Terms of Service</Link> and <Link to="/privacy">Privacy Policy</Link>
                    </p>
                  </>
                )}
              </section>
            </div>
          </div>
        </aside>

        {/* ========== FEATURES ========== */}
        <section className="cra-features" id="cra-features" aria-labelledby="cra-features-title">
          <div className="cra-section-head">
            <div className="cra-eyebrow cra-reveal">Features</div>
            <h2 className="cra-section-title cra-reveal" id="cra-features-title" style={{ '--i': 1 }}>Automate Your Emission Reporting</h2>
          </div>
          <div className="cra-cards">
            {FEATURES.map((f, idx) => (
              <article key={f.title} className="cra-card cra-glass cra-reveal" style={{ '--i': idx + 2 }}>
                <span className="cra-num">{String(idx + 1).padStart(2, '0')}</span>
                <div className="cra-icon">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d={f.path} /></svg>
                </div>
                <h3>{f.title}</h3>
                <p>{f.description}</p>
              </article>
            ))}
          </div>
        </section>

        {/* ========== FOOTER ========== */}
        <footer className="cra-foot">
          <div className="cra-foot-inner cra-glass cra-reveal">
            <div className="cra-foot-brand">
              <img src={LOGO} alt="Arantree" />
              <div><span>Powered by</span><strong>Arantree Consulting Services</strong></div>
            </div>
            <p>For queries contact: <a href="mailto:info@arantree.com">info@arantree.com</a></p>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default Auth;
